<template>
  <b-modal ref="picker"
           hide-header
           hide-footer
           centered
           size="lg"
           modal-class="project-launcher-modal"
           @hidden="reset">
    <div class="project-launcher scope-picker">
      <div class="project-launcher__head">
        <span class="project-launcher__icon project-launcher__icon--teal"><font-awesome-icon :icon="config.icon"/></span>
        <div class="grow">
          <h2>{{ $t('projects.scope_tab.picker.title') }}</h2>
          <div class="project-launcher__sub">{{ $t(config.label) }} · {{ $t('projects.scope_tab.picker.sub') }}</div>
        </div>
        <span class="project-launcher__close" @click="hide"><font-awesome-icon icon="times"/></span>
      </div>

      <div class="project-launcher__body">
        <div class="project-launcher__search">
          <font-awesome-icon icon="search"/>
          <input type="text"
                 ref="search"
                 v-model="keyword"
                 @input="debouncedLoad"
                 :placeholder="$t('projects.scope_tab.picker.search')"
                 qid="scope-picker-search">
          <font-awesome-icon icon="spinner" class="fa-spin" v-if="loading"/>
        </div>

        <div class="scope-picker__bar">
          <span class="scope-picker__tag"
                v-for="tag in tags"
                :key="tag"
                :class="{'on': tagFilter === tag}"
                @click="tagFilter = tagFilter === tag ? '' : tag">
            <font-awesome-icon icon="tag"/>{{ tag }}
          </span>
          <span class="grow"></span>
          <span class="pm-link"
                v-if="uncheckedShown.length"
                @click="setShown(true)"
                qid="scope-picker-check-all">
            {{ $t('projects.scope_tab.picker.check_shown', {count: uncheckedShown.length}) }}
          </span>
          <span class="pm-link ml-3"
                v-if="checkedShown.length"
                @click="setShown(false)"
                qid="scope-picker-uncheck-all">
            {{ $t('projects.scope_tab.picker.uncheck_shown', {count: checkedShown.length}) }}
          </span>
        </div>

        <div class="scope-picker__list" qid="scope-picker-list">
          <div class="pm-empty" v-if="!loading && !shown.length">{{ $t('projects.scope_tab.picker.none_found') }}</div>
          <div class="scope-picker__row"
               v-for="item in shown"
               :key="item.id"
               :class="{'on': isChecked(item), 'adding': isAdding(item), 'removing': isRemoving(item)}"
               @click="toggle(item)"
               qid="scope-picker-row"
          >
            <span class="project-launcher__check" :class="{'scope-picker__check--on': isChecked(item)}">
              <font-awesome-icon icon="check" v-if="isChecked(item)"/>
            </span>
            <div class="grow">
              <div class="scope-picker__name">{{ item.name }}</div>
              <div class="scope-picker__meta">
                <span v-if="item.process && item.process.name"><font-awesome-icon icon="cube" class="mr-1"/>{{ item.process.name }}</span>
                <span class="scope-picker__tags" v-if="item.tags_translated && item.tags_translated.length">
                  <span v-for="tag in item.tags_translated.slice(0, 3)" :key="tag.id || tag.name_translated">{{ tag.name_translated }}</span>
                </span>
              </div>
            </div>
            <span class="pm-tag pm-tag--teal" v-if="isAdding(item)">{{ $t('projects.scope_tab.picker.will_add') }}</span>
            <span class="pm-tag pm-tag--red" v-if="isRemoving(item)">{{ $t('projects.scope_tab.picker.will_remove') }}</span>
          </div>
          <div class="scope-picker__more" v-if="total > results.length">{{ $t('projects.scope_tab.picker.more', {shown: results.length, total: total}) }}</div>
        </div>
      </div>

      <div class="project-launcher__foot">
        <div class="scope-picker__summary">
          <span v-if="!toAdd.length && !toRemove.length">{{ $tc('projects.scope_tab.count', checkedCount, {count: checkedCount}) }}</span>
          <span class="pm-tag pm-tag--teal" v-if="toAdd.length">+{{ toAdd.length }}</span>
          <span class="pm-tag pm-tag--red" v-if="toRemove.length">−{{ toRemove.length }}</span>
          <span v-if="toRemove.length">{{ $t('projects.scope_tab.picker.remove_warning') }}</span>
        </div>
        <span class="grow"></span>
        <b-button variant="secondary" @click="hide">{{ $t('projects.create.cancel') }}</b-button>
        <b-button variant="success"
                  class="ml-3"
                  :disabled="(!toAdd.length && !toRemove.length) || saving"
                  @click="save"
                  qid="scope-picker-save">
          <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin mr-1"/>
          {{ $t('tasks.modal.save') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import { scopeKind, kindParams } from '@/views/Projects/Scope/scopeKinds.js'

  // Every asset of a type with a tick for being in the scope: tick to add, untick to remove, save once (US 9396)
  export default {
    name: 'AssetPicker',
    props: {
      project: {type: Object, required: true},
      kind: {type: String, required: true},
      // The project's scope items of this kind (project models)
      scopeItems: {type: Array, default: function() { return []; }}
    },
    data: function() {
      return {
        keyword: '',
        results: [],
        total: 0,
        // Asset id => ticked, for every asset the user changed
        changes: {},
        tagFilter: '',
        loading: false,
        saving: false,
        timer: null
      }
    },
    computed: {
      config() {
        return scopeKind(this.kind);
      },
      getClient() {
        return this.$store.state.client;
      },
      // Asset id => project model id of what is in scope now
      inScope() {
        let map = {};
        _.forEach(this.scopeItems, function(item) {
          if (item.model_id) {
            map[item.model_id] = item.id;
          }
        });
        return map;
      },
      tags() {
        return _.uniq(_.flatMap(this.results, function(item) {
          return _.map(item.tags_translated || [], 'name_translated');
        })).slice(0, 8);
      },
      shown() {
        if (!this.tagFilter) {
          return this.results;
        }
        return _.filter(this.results, (item) => _.some(item.tags_translated || [], {name_translated: this.tagFilter}));
      },
      checkedShown() {
        return _.filter(this.shown, (item) => this.isChecked(item));
      },
      uncheckedShown() {
        return _.filter(this.shown, (item) => !this.isChecked(item));
      },
      toAdd() {
        return _.filter(_.keys(this.changes), (id) => this.changes[id] && !this.inScope[id]);
      },
      toRemove() {
        return _.filter(_.keys(this.changes), (id) => !this.changes[id] && this.inScope[id]);
      },
      checkedCount() {
        return _.keys(this.inScope).length + this.toAdd.length - this.toRemove.length;
      }
    },
    methods: {
      show() {
        this.$refs.picker.show();
        this.load();
        this.$nextTick(() => {
          if (this.$refs.search) {
            this.$refs.search.focus();
          }
        });
      },
      hide() {
        this.$refs.picker.hide();
      },
      reset() {
        this.keyword = '';
        this.changes = {};
        this.tagFilter = '';
      },
      isChecked(item) {
        return this.changes[item.id] !== undefined ? this.changes[item.id] : !!this.inScope[item.id];
      },
      isAdding(item) {
        return this.isChecked(item) && !this.inScope[item.id];
      },
      isRemoving(item) {
        return !this.isChecked(item) && !!this.inScope[item.id];
      },
      setChecked(item, checked) {
        // Back to how it is in the scope means no change
        if (checked === !!this.inScope[item.id]) {
          this.$delete(this.changes, item.id);
        } else {
          this.$set(this.changes, item.id, checked);
        }
      },
      toggle(item) {
        this.setChecked(item, !this.isChecked(item));
      },
      setShown(checked) {
        _.forEach(this.shown, (item) => this.setChecked(item, checked));
      },
      debouncedLoad() {
        clearTimeout(this.timer);
        this.timer = setTimeout(this.load, 300);
      },
      load() {
        this.loading = true;
        let params = Object.assign({
          client: this.getClient.slug,
          keyword: this.keyword || undefined,
          per_page: 200
        }, kindParams(this.kind, this.project));
        piincHttp.get(this.config.endpoint, {params: params}).then((response) => {
          this.results = response.data.items || [];
          this.total = response.data.total_count || this.results.length;
          this.loading = false;
        }, () => {
          this.loading = false;
        });
      },
      save() {
        let base = 'projects/' + this.project.slug + '/models';
        let added = this.toAdd.length;
        let removed = this.toRemove.length;
        let requests = [];
        if (added) {
          requests.push(piincHttp.post(base, {items: _.map(this.toAdd, (id) => {
            return {model: this.config.modelType, id: parseInt(id, 10)};
          })}));
        }
        _.forEach(this.toRemove, (id) => {
          requests.push(piincHttp.delete(base + '/' + this.inScope[id]));
        });
        this.saving = true;
        Promise.all(requests).then(() => {
          this.saving = false;
          this.$toastr('success', this.$t('projects.scope_tab.picker.saved', {added: added, removed: removed}));
          this.hide();
          this.$emit('changed');
        }, (error) => {
          this.saving = false;
          this.$toastr('error', error.data && error.data.message ? error.data.message : this.$t('projects.launcher.error_generic'));
          this.$emit('changed');
        });
      }
    }
  }
</script>
